import React, { useState, useEffect } from "react";
import { useCampaigns } from "@/hooks/useCampaigns";
import * as Icons from "@/components/ui/shared/Icons";
import clsx from "clsx";

// Define AccountCampaign type directly to avoid Database type conflicts
type AccountCampaign = {
  id: string
  account_id: string
  name: string
  assistant_id: string | null
  trigger_type: string
  trigger_keywords: string[] | null
  tags: string[] | null
  pipeline_id: string | null
  pipeline_stage_id: string | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

interface CampaignSearchProps {
  onCampaignSelect?: (campaign: AccountCampaign) => void;
  onSearchChange?: (searchTerm: string) => void;
  placeholder?: string;
  styles?: string;
}

const CampaignSearch: React.FC<CampaignSearchProps> = ({ 
  onCampaignSelect, 
  onSearchChange,
  placeholder = "Pesquisar campanhas...",
  styles = "w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] min-h-[40px]"
}) => {
  const { campaigns } = useCampaigns();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCampaigns, setFilteredCampaigns] = useState<AccountCampaign[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCampaigns([]);
      setShowResults(false);
      return;
    }

    const filtered = campaigns.filter(campaign => {
      const name = campaign.name.toLowerCase();
      const search = searchTerm.toLowerCase();
      
      return name.includes(search) || 
             campaign.trigger_keywords?.some((keyword: string) => 
               keyword.toLowerCase().includes(search)
             );
    });

    console.log('🔍 [CampaignSearch] Filtrando campanhas:', {
      searchTerm,
      total_campaigns: campaigns.length,
      filtered_count: filtered.length
    });

    setFilteredCampaigns(filtered.slice(0, 10)); // Limitar a 10 resultados
    setShowResults(true);
  }, [searchTerm, campaigns]);

  const handleCampaignClick = (campaign: AccountCampaign) => {
    console.log('👆 [CampaignSearch] Campanha selecionada:', campaign);
    onCampaignSelect?.(campaign);
    setSearchTerm(campaign.name);
    setShowResults(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    onSearchChange?.(e.target.value);
  };

  const handleInputFocus = () => {
    if (searchTerm.trim() && filteredCampaigns.length > 0) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    // Delay para permitir clique nos resultados
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="relative">
      <div
        className={clsx(
          "gap-2 rounded-[6px] border border-neutral-500 bg-neutral-600 px-3 flex justify-start items-center",
          styles
        )}
      >
        <Icons.search className="w-[16px] h-[16px]" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="grow-1 text-[16px] font-normal text-neutral-50 placeholder:text-neutral-400 w-full h-full border-none focus:outline-none bg-transparent"
        />
      </div>

      {/* Resultados da busca */}
      {showResults && filteredCampaigns.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-neutral-600 border border-neutral-500 rounded-[6px] shadow-lg z-50 max-h-[300px] overflow-y-auto">
          {filteredCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              onClick={() => handleCampaignClick(campaign)}
              className="flex items-center gap-3 p-3 hover:bg-neutral-700 cursor-pointer transition-colors border-b border-neutral-500 last:border-b-0"
            >
              <div className="w-[32px] h-[32px] bg-neutral-400 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-neutral-800">
                  {campaign.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <p className="text-[14px] font-medium text-neutral-50 truncate">{campaign.name}</p>
                <p className="text-[12px] text-neutral-400 truncate">
                  {campaign.trigger_type === 'any' ? 'Qualquer mensagem' : 
                   campaign.trigger_type === 'contains' ? 'Contém palavras-chave' : 
                   'Inicia com palavras-chave'}
                </p>
              </div>
              {campaign.is_active ? (
                <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded">
                  Ativa
                </span>
              ) : (
                <span className="text-xs bg-neutral-500 text-neutral-300 px-2 py-1 rounded">
                  Inativa
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignSearch;